/**
 * Shared screen header component.
 * Every screen gets: Title (left) + All currencies (right).
 */

import { StateManager } from '../../managers';
import { Tooltip } from './Tooltip';
import { renderCurrencyIcon } from './ItemIcon';
import type { PlayerData, ConfigData, CurrencyDef } from '../../types';

/** Generate the static header HTML for use inside createDOM(). */
export function screenHeaderHTML(title: string, extraHTML = ''): string {
  return `
    <div class="screen-header">
      <span class="screen-title">${title}</span>
      ${extraHTML}
      <div class="screen-header-currencies"></div>
    </div>
  `;
}

/** Render currency badges into the header. Call from onRender() with the screen's rootEl.
 *  On the first call, builds the badge DOM. On subsequent calls, updates each
 *  badge in place — if a value changed, the displayed number tweens up/down
 *  over ~500ms instead of jumping, and the icon briefly pulses.
 *
 *  Filtering precedence (most → least specific):
 *  1. `only` — explicit ID allowlist (e.g. Soulloom passes `['soulthread']`).
 *  2. `screenId` — uses each currency's `visibleOn` array. Currencies with
 *     `visibleOn` undefined are universal (e.g. Gold). Currencies with a
 *     defined `visibleOn` array show only when `screenId` is included.
 *  3. Neither — falls back to the previous behavior (every currency shown).
 */
export function renderScreenCurrencies(root: HTMLElement, screenId?: string, only?: string[]): void {
  const el = root.querySelector('.screen-header-currencies') as HTMLElement | null;
  if (!el) return;

  const player = StateManager.get('playerData') as PlayerData | undefined;
  const configData = StateManager.get('configData') as ConfigData | undefined;
  const currencies = configData?.currencies;
  if (!player || !currencies) return;

  const allSorted = Object.values(currencies).sort((a, b) => a.displayOrder - b.displayOrder);
  let sorted: CurrencyDef[];
  if (only) {
    sorted = allSorted.filter(c => only.includes(c.id));
  } else if (screenId) {
    sorted = allSorted.filter(c => !c.visibleOn || c.visibleOn.includes(screenId));
  } else {
    sorted = allSorted;
  }

  // Build badges on first render. After that, just update values in place.
  const needsBuild = el.children.length !== sorted.length;
  if (needsBuild) {
    el.innerHTML = sorted.map((cur: CurrencyDef) => {
      const amount = (player as any)[cur.id] ?? 0;
      return `<span class="screen-currency" data-currency-id="${cur.id}" data-current-value="${amount}"><span class="screen-currency-icon">${renderCurrencyIcon(cur.id)}</span><span class="screen-currency-amount">${amount}</span></span>`;
    }).join('');

    // Attach tooltips on initial build
    el.querySelectorAll('.screen-currency').forEach(badge => {
      const id = (badge as HTMLElement).dataset.currencyId;
      const cur = id ? currencies[id] : null;
      if (cur) {
        Tooltip.attach(badge as HTMLElement, () =>
          `<div class="tooltip-inner"><div class="tooltip-title">${renderCurrencyIcon(cur.id, 20)} ${cur.name}</div><div class="tooltip-desc">${cur.description}</div></div>`
        );
      }
    });
    return;
  }

  // Update path: tween any changed values, leave the rest alone.
  for (const cur of sorted) {
    const badge = el.querySelector(`[data-currency-id="${cur.id}"]`) as HTMLElement | null;
    if (!badge) continue;
    const amountEl = badge.querySelector('.screen-currency-amount') as HTMLElement | null;
    if (!amountEl) continue;

    const newValue = (player as any)[cur.id] ?? 0;
    const currentValue = Number(badge.dataset.currentValue ?? '0');
    if (newValue === currentValue) continue;

    // Pulse the icon when the value changes (especially when it goes up)
    if (newValue > currentValue) {
      badge.classList.remove('is-pulsing');
      // Force reflow so re-adding the class restarts the animation
      void badge.offsetWidth;
      badge.classList.add('is-pulsing');
    }

    tweenNumber(amountEl, currentValue, newValue, 500);
    badge.dataset.currentValue = String(newValue);
  }
}

/** rAF-based tween that updates an element's text from `from` → `to` over `duration` ms.
 *  Uses ease-out cubic so the count-up decelerates near the end (feels premium). */
function tweenNumber(el: HTMLElement, from: number, to: number, duration: number): void {
  const start = performance.now();
  const delta = to - from;
  const step = (now: number) => {
    const t = Math.min(1, (now - start) / duration);
    // ease-out cubic
    const eased = 1 - Math.pow(1 - t, 3);
    const value = Math.round(from + delta * eased);
    el.textContent = String(value);
    if (t < 1) requestAnimationFrame(step);
    else el.textContent = String(to);
  };
  requestAnimationFrame(step);
}
